import React, {useEffect, useState} from "react";
import {Button} from "primereact/button";
import {DataTable} from "primereact/datatable";
import {Column} from "primereact/column";
import DatasetUnitSelector from "components/basic/dropdowns/dataset-unit-selector/DatasetUnitSelector";
import {Dropdown} from "primereact/dropdown";
import {InputText} from "primereact/inputtext";
import {InputNumber} from "primereact/inputnumber";
import {easasAlert} from "components/fragments/easas-global-fragment/EasasGlobalAlerts";
import {useUpdateDatasetInfo} from "api/api-hooks/datasets-hooks";
import {useGetDatasetMappersData} from "api/api-hooks/dataset-mapper-hooks";
import {DatasetStringValueMapperDTO,} from "components/dashboard/charts/interfaces";
import {DatasetInfo} from "api/model/dataset/DatasetInfo";
import {DATASET_ACCESS_TYPES, DATASET_DATA_TYPES,} from "../create-dataset-dialog/CreateDatasetDialog";

interface IEditDatasetProps {
  selectedDatasetID: DatasetInfo[];
  setEditDatasetDialogVisibility: (value: boolean) => void;
  setSelectedDatasets: (arr: Array<DatasetInfo>) => void;
}

const EditDatasetDialog = ({
  selectedDatasetID,
  setEditDatasetDialogVisibility,
  setSelectedDatasets,
}: IEditDatasetProps) => {
  const [datasetsToUpdate, setDatasetsToUpdate] = useState<DatasetInfo[]>([]);
  const datasetsArray = selectedDatasetID.map((dataset, index) => {
    return { ...dataset, id: index + 1 };
  });
  const [datasetsArr, setDatasetsArr] = useState<DatasetInfo[]>([
    ...datasetsArray,
  ]);
  const [mappers, setMappers] = useState<DatasetStringValueMapperDTO[]>([]);

  const {
    data: currentMappers,
    isLoading,
    isError,
  } = useGetDatasetMappersData();

  useEffect(() => {
    if (!isLoading && !isError) {
      setMappers([...currentMappers]);
    }
  }, [currentMappers, isLoading, isError]);

  const titleBodyTemplate = (options: DatasetInfo) => {
    if (options.id) {
      const currentTitle = datasetsArr[options.id - 1]?.title || "";
      return (
        <InputText
          className="w-full"
          value={currentTitle}
          onChange={(e) => inputChangeHandler(options, e.target, "title")}
        />
      );
    }
  };

  const dataTypeBodyTemplate = (options: DatasetInfo) => {
    if (options.id) {
      const currentDataType = datasetsArr[options.id - 1]?.dataType || "";
      return (
        <Dropdown
          className="w-full"
          options={DATASET_DATA_TYPES}
          value={currentDataType}
          onChange={(e) => inputChangeHandler(options, e, "dataType")}
        />
      );
    }
  };

  const accessTypeBodyTemplate = (options: DatasetInfo) => {
    if (options.id) {
      const currentAccessType = datasetsArr[options.id - 1]?.accessType || "";
      return (
        <Dropdown
          className="w-full"
          options={DATASET_ACCESS_TYPES}
          value={currentAccessType}
          onChange={(e) => inputChangeHandler(options, e, "accessType")}
        />
      );
    }
  };

  const mapperBodyTemplate = (options: DatasetInfo) => {
    if (options.id) {
      const currentMapper = mappers.find(
        (mapper) => mapper.id == datasetsArr[options.id - 1]?.stringValueMapper
      ) || { id: "", title: "" };

      return (
        <Dropdown
          className="w-full"
          options={mappers.map((mapper: any) => ({
            label: mapper.title,
            value: mapper.id,
          }))}
          value={currentMapper.id}
          onChange={(e) => inputChangeHandler(options, e, "stringValueMapper")}
        />
      );
    }
  };

  const unitBodyTemplate = (options: DatasetInfo) => {
    if (options.id) {
      const currentUnit = datasetsArr[options.id - 1]?.unit || "";
      return (
        <DatasetUnitSelector
          className="w-full"
          value={currentUnit}
          onChange={(e) => inputChangeHandler(options, e, "unit")}
        />
      );
    }
  };

  const decimalPlacesBodyTemplate = (options: DatasetInfo) => {
    if (options.id) {
      const currentDecimalPlaces =
        datasetsArr[options.id - 1]?.decimalPlaces || 0;
      return (
        <InputNumber
          value={currentDecimalPlaces}
          onValueChange={(e) => inputChangeHandler(options, e, "decimalPlaces")}
          min={0}
          max={10}
          showButtons
          inputStyle={{ width: "100%" }}
        />
      );
    }
  };

  const deviceIdBodyTemplate = (options: DatasetInfo) => {
    if (options.id) {
      const currentDeviceId = datasetsArr[options.id - 1]?.deviceId || 0;
      return (
        <InputNumber
          value={currentDeviceId}
          onValueChange={(e) => inputChangeHandler(options, e, "deviceId")}
          inputStyle={{ width: "100%" }}
        />
      );
    }
  };

  const inputChangeHandler = (options: DatasetInfo, e: any, field: keyof DatasetStringValueMapperDTO) => {
    setDatasetsArr((prevState) => {
      const updatedDatasets = [...prevState];
      (updatedDatasets[options.id! - 1] as { [key: string]: any })[field] =
        e.value;
      return updatedDatasets;
    });

    setDatasetsToUpdate((prevState: DatasetInfo[]) => {
      const isUnique = !prevState.some(
        (dataset) => dataset.identifier === options.identifier
      );
      const index = prevState.findIndex(
        (obj) => obj.identifier === options.identifier
      );
      if (isUnique) {
        return [...prevState, options];
      } if (index !== -1) {
        const updatedDatasets = [...prevState];
        updatedDatasets[index] = { ...updatedDatasets[index], ...options };
        return updatedDatasets;
      } 
        return prevState;
      
    });
  };

  const { mutateAsync } = useUpdateDatasetInfo(true);

  const saveChangesHandler = async () => {
    try {
      if (datasetsToUpdate.length === 0) {
          easasAlert({
            summary: "No changes detected",
            detail: "Please make changes before saving.",
            severity: "warn",
          });
        return;
      }

      const results = await Promise.allSettled(
        datasetsToUpdate.map(async (dataset) => {
          return mutateAsync(dataset);
        })
      );

      const successFullMutations = results.filter(
        (result) => result.status === "fulfilled"
      );
      const failedMutations = results.filter(
        (result) => result.status === "rejected"
      );

            if (successFullMutations.length === results.length) {
                easasAlert({
                  summary: "Success",
                  detail: "All changes were saved successfully.",
                  severity: "success",
                });
            } else {
                easasAlert({
                  summary: "Error",
                  detail: `${failedMutations.length} datasets failed to update`,
                  severity: "error",
                });
            }
        } catch (e) {
            console.log('Error updating datasets', e)
            easasAlert({
              summary: "Error",
              detail: "An error occurred while updating datasets",
              severity: "error",
            });
        }

    setEditDatasetDialogVisibility(false);
    setSelectedDatasets([]);
  };

  return (
    <div className="flex flex-column gap-3">
      <DataTable
        value={datasetsArr}
        className="p-datatable-gridlines mb-5"
        style={{ width: "100%" }}
      >
        <Column
          field="title"
          header="Title"
          style={{ padding: 0, width: "40%" }}
          body={titleBodyTemplate}
        />
        <Column
          field="dataType"
          header="Type"
          style={{ padding: 0, width: "10%" }}
          body={dataTypeBodyTemplate}
        />
        <Column
          field="accessType"
          header="Access"
          style={{ padding: 0, width: "10%" }}
          body={accessTypeBodyTemplate}
        />
        <Column
          field="mapper"
          header="Mapper"
          style={{ padding: 0, width: "10%" }}
          body={mapperBodyTemplate}
        />
        <Column
          field="unit"
          header="Unit"
          style={{ padding: 0, width: "10%" }}
          body={unitBodyTemplate}
        />
        <Column
          field="decimalPlaces"
          header="Decimal Places"
          style={{ padding: 0, width: "10%" }}
          body={decimalPlacesBodyTemplate}
        />
        <Column
          field="deviceId"
          header="Device ID"
          style={{ padding: 0, width: "10%" }}
          body={deviceIdBodyTemplate}
        />
      </DataTable>
      <div className="buttons flex gap-2 mt-2">
        <Button
          label="Save"
          icon="pi pi-check"
          onClick={saveChangesHandler}
          className="p-button-success"
        />
        <Button
          label="Cancel"
          icon="pi pi-times"
          onClick={() => setEditDatasetDialogVisibility(false)}
          className="p-button-danger"
        />
      </div>
    </div>
  );
};

export default EditDatasetDialog;
